/**
 * Pure layout / zoom geometry for the chrome viewer and viewport fit mode.
 * Keeps numeric policy in one place for tests and flicker-related tweaks.
 */

export type Size = { width: number; height: number }

type PhotoBox = { w: number; h: number }

/** Height ÷ width (display aspect), or 0 if invalid. */
export function photoDisplayAspectRatio(photo: PhotoBox | undefined): number {
  if (!photo || photo.w <= 0 || photo.h <= 0) return 0
  return photo.h / photo.w
}

/** Width ÷ height — matches in-player `photoAspect` naming. */
export function photoAspectWidthOverHeight(photo: PhotoBox): number {
  return photo.w / photo.h
}

/**
 * Letterboxed fit: width (px) of the photo when scaled to fit inside the viewport.
 */
export function fitPhotoWidthInViewport(photo: PhotoBox, viewport: Size): number {
  const pa = photoAspectWidthOverHeight(photo)
  const va = viewport.width / viewport.height
  if (pa > va) {
    return viewport.width
  }
  return Math.round(viewport.height * pa)
}

/** `zoomLevel` that makes the photo fill the viewport in fit mode (viewer width ÷ native photo width). */
export function fitZoomLevelForViewport(photo: PhotoBox, viewport: Size): number {
  return fitPhotoWidthInViewport(photo, viewport) / photo.w
}

/**
 * Same as {@link fitZoomLevelForViewport} but returns `undefined` when the viewport has no area.
 */
export function fitZoomLevelForViewportOrUndefined(
  photo: PhotoBox,
  viewportWidth: number,
  viewportHeight: number,
): number | undefined {
  if (viewportWidth <= 0 || viewportHeight <= 0) return undefined
  return fitZoomLevelForViewport(photo, { width: viewportWidth, height: viewportHeight })
}

export type ChromeViewerWidthParams = {
  zoomFit: boolean
  currentPhoto: PhotoBox | undefined
  viewportSize: Size
  zoomLevel: number
  externalWidth: number | undefined
  containerWidth: number | undefined
}

/**
 * Viewer strip width and optional fit zoom — mirrors `useAevocamPlayer` chrome sizing.
 */
export function computeChromeViewerWidth(params: ChromeViewerWidthParams): {
  viewerWidth: number | undefined
  calculatedFitZoom: number | undefined
} {
  const { zoomFit, currentPhoto, viewportSize, zoomLevel, externalWidth, containerWidth } = params

  if (
    zoomFit &&
    currentPhoto &&
    viewportSize.width > 0 &&
    viewportSize.height > 0
  ) {
    const calculatedViewerWidth = fitPhotoWidthInViewport(currentPhoto, viewportSize)
    return {
      viewerWidth: calculatedViewerWidth,
      calculatedFitZoom: calculatedViewerWidth / currentPhoto.w,
    }
  }

  if (currentPhoto) {
    return {
      viewerWidth: Math.round(currentPhoto.w * zoomLevel),
      calculatedFitZoom: undefined,
    }
  }

  const baseWidth = externalWidth !== undefined ? externalWidth : containerWidth
  return { viewerWidth: baseWidth, calculatedFitZoom: undefined }
}

export function viewerLayoutDimensions(
  photo: PhotoBox,
  zoomFit: boolean,
  zoomLevel: number,
  viewport: Size,
): { width: number; height: number } | null {
  if (viewport.width <= 0 || viewport.height <= 0) return null
  const width = zoomFit
    ? fitPhotoWidthInViewport(photo, viewport)
    : Math.round(photo.w * zoomLevel)
  const height = Math.round(width * (photo.h / photo.w))
  return { width, height }
}

/** Whether the laid-out viewer rectangle fits entirely inside the viewport. */
export function viewerFitsInsideViewport(
  photo: PhotoBox,
  zoomFit: boolean,
  zoomLevel: number,
  viewport: Size,
): boolean {
  const dims = viewerLayoutDimensions(photo, zoomFit, zoomLevel, viewport)
  if (!dims) return false
  return dims.width <= viewport.width && dims.height <= viewport.height
}

/**
 * Same clamp as `AevocamViewport` drag: keep at least `minVisiblePx` of the viewer inside the viewport.
 */
export function clampPanToKeepViewerVisible(
  nextPanX: number,
  nextPanY: number,
  viewport: Size,
  viewer: Size,
  minVisiblePx = 100,
): { panX: number; panY: number } {
  const vw = viewport.width
  const vh = viewport.height
  const iw = viewer.width
  const ih = viewer.height
  if (vw <= 0 || vh <= 0 || iw <= 0 || ih <= 0) {
    return { panX: nextPanX, panY: nextPanY }
  }

  const minVisibleW = Math.min(minVisiblePx, iw, vw)
  const minVisibleH = Math.min(minVisiblePx, ih, vh)
  const maxAbsPanX = (vw + iw) / 2 - minVisibleW
  const maxAbsPanY = (vh + ih) / 2 - minVisibleH

  return {
    panX: Math.min(Math.max(nextPanX, -maxAbsPanX), maxAbsPanX),
    panY: Math.min(Math.max(nextPanY, -maxAbsPanY), maxAbsPanY),
  }
}
